import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import * as XLSX from 'xlsx';

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  state: string;
  purchase_price: string;
  broker: string;
  last_contact: string;
  client_state: string;
  date_created: string;
  user_id: string;
  loan_type: string;
  lead_source: string;
  lender_name: string;
  loan_amount?: string;
  client_state_updated?: string;
  realtor_id?: string;
  closing_date?: string;
  comp?: string;
  interest_rate?: string;
  lender_loan_number?: string;
  arive_loan_number?: string;
}

interface Realtor {
  id: string;
  name: string;
  email: string;
  phone: string;
  state: string;
}

interface CompRate {
  id: string;
  broker: string;
  comp_percentage: number;
  personal_comp_percentage: number;
  full_broker_name: string;
}

interface DiscoveryCallField {
  id: string;
  field_name: string;
  field_label: string;
  field_type: string;
  dropdown_options: string[] | null;
  display_order: number;
  is_required: boolean;
}

const parsePurchasePrice = (priceString: string): number => {
  const cleaned = priceString.replace(/[$,]/g, '');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const formatPurchasePrice = (price: string): string => {
  const cleaned = price.replace(/[$,]/g, '');
  const parsed = parseFloat(cleaned);
  if (isNaN(parsed)) return price;
  return `$${parsed.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const getStateAbbreviation = (state: string): string => {
  const stateMap: Record<string, string> = {
    'ALABAMA': 'AL', 'ALASKA': 'AK', 'ARIZONA': 'AZ', 'ARKANSAS': 'AR', 'CALIFORNIA': 'CA',
    'COLORADO': 'CO', 'CONNECTICUT': 'CT', 'DELAWARE': 'DE', 'FLORIDA': 'FL', 'GEORGIA': 'GA',
    'HAWAII': 'HI', 'IDAHO': 'ID', 'ILLINOIS': 'IL', 'INDIANA': 'IN', 'IOWA': 'IA',
    'KANSAS': 'KS', 'KENTUCKY': 'KY', 'LOUISIANA': 'LA', 'MAINE': 'ME', 'MARYLAND': 'MD',
    'MASSACHUSETTS': 'MA', 'MICHIGAN': 'MI', 'MINNESOTA': 'MN', 'MISSISSIPPI': 'MS', 'MISSOURI': 'MO',
    'MONTANA': 'MT', 'NEBRASKA': 'NE', 'NEVADA': 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
    'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', 'OHIO': 'OH',
    'OKLAHOMA': 'OK', 'OREGON': 'OR', 'PENNSYLVANIA': 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD', 'TENNESSEE': 'TN', 'TEXAS': 'TX', 'UTAH': 'UT', 'VERMONT': 'VT',
    'VIRGINIA': 'VA', 'WASHINGTON': 'WA', 'WEST VIRGINIA': 'WV', 'WISCONSIN': 'WI', 'WYOMING': 'WY'
  };
  
  const upperState = state.toUpperCase().trim();
  if (upperState.length === 2) return upperState;
  return stateMap[upperState] || state;
};

const formatDateTime = (dateString: string): string => {
  if (!dateString || dateString === '-') return '-';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return dateString;
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString || dateString === '-') return '-';
  
  try {
    // Parse date without timezone conversion
    const date = new Date(dateString + 'T00:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${month}/${day}/${year}`;
  } catch (error) {
    return dateString;
  }
};

export default function ClosedPage() {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [clients, setClients] = useState<Client[]>([]);
  const [realtors, setRealtors] = useState<Realtor[]>([]);
  const [compRates, setCompRates] = useState<CompRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Add mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Add user profile state
  const [userFullName, setUserFullName] = useState<string>('');

  // Add missing state variables
  const [clientStateOptions, setClientStateOptions] = useState<string[]>([]);
  const [interestRateFilter, setInterestRateFilter] = useState<string>('');
  const [monthsSinceCloseFilter, setMonthsSinceCloseFilter] = useState<string>('');
  const [tempInterestRateFilter, setTempInterestRateFilter] = useState<string>('Select Rate');
  const [tempMonthsSinceCloseFilter, setTempMonthsSinceCloseFilter] = useState<string>('Make Selection');
  
  // Desktop filter states
  const [desktopInterestRateFilter, setDesktopInterestRateFilter] = useState<string>('');
  const [desktopMonthsSinceCloseFilter, setDesktopMonthsSinceCloseFilter] = useState<string>('');
  const [tempDesktopInterestRateFilter, setTempDesktopInterestRateFilter] = useState<string>('Select Rate');
  const [tempDesktopMonthsSinceCloseFilter, setTempDesktopMonthsSinceCloseFilter] = useState<string>('Make Selection');

  // Add date range filter states
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [tempDateFromFilter, setTempDateFromFilter] = useState<string>('');
  const [tempDateToFilter, setTempDateToFilter] = useState<string>('');

  // Add client info dialog state
  const [showClientInfoDialog, setShowClientInfoDialog] = useState(false);
  const [clientInfoDialogClient, setClientInfoDialogClient] = useState<Client | null>(null);

  // Add lender options state
  const [lenderOptions, setLenderOptions] = useState<string[]>([]);

  // Add state for editing lender with custom option
  const [editingLenderName, setEditingLenderName] = useState<string>('');
  const [editingCustomLender, setEditingCustomLender] = useState<string>('');

  // Add state for broker comp
  const [editingBrokerComp, setEditingBrokerComp] = useState<string>('');

  // Add search state
  const [desktopSearchQuery, setDesktopSearchQuery] = useState<string>('');
  const [mobileSearchQuery, setMobileSearchQuery] = useState<string>('');

  // Add sorting state
  const [sortField, setSortField] = useState<keyof Client | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Add state for column widths
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    name: 150,
    state: 80,
    purchase_price: 120,
    interest_rate: 100,
    broker_comp: 120,
    lead_source: 120,
    lender_name: 120,
    lender_loan_number: 120,
    broker: 100,
    closing_date: 120,
    client_state: 150,
    actions: 100
  });

  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState<number>(0);
  const [resizeStartWidth, setResizeStartWidth] = useState<number>(0);

  // Handle column resize
  const handleResizeStart = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    setResizingColumn(columnKey);
    setResizeStartX(e.clientX);
    setResizeStartWidth(columnWidths[columnKey] || 150);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!resizingColumn) return;
    const diff = e.clientX - resizeStartX;
    const newWidth = Math.max(80, resizeStartWidth + diff);
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  };

  const handleResizeEnd = () => {
    setResizingColumn(null);
  };

  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  const loadLenderNames = async () => {
    const { data, error } = await supabase
      .from('lender_names')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading lender names:', error);
      return;
    }

    if (data && data.length > 0) {
      setLenderOptions(data.map(l => l.lender_name));
    }
  };

  const loadCompRates = async () => {
    const { data, error } = await supabase
      .from('comp_rates')
      .select('*')
      .order('broker', { ascending: true });

    if (error) {
      console.error('Error loading comp rates:', error);
      return;
    }

    if (data) {
      setCompRates(data);
    }
  };

  const loadClientStates = async () => {
    const { data, error } = await supabase
      .from('client_states')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading client states:', error);
      return;
    }

    if (data && data.length > 0) {
      setClientStateOptions(data.map(s => s.state_name));
    } else {
      // Fallback to default states if none in database
      setClientStateOptions([
        'Lead Received; Not Yet Contacted',
        'Lead Received; Contacted; No Response',
        'Application Sent, Awaiting Completion',
        'Application Completed; Pre-Approval/VAL in Process',
        'Pre-Approved/VAL Complete; House Shopping',
        'Offer Accepted; Loan In Process',
        'Closed',
        'On Hold; Will Contact at Later Date',
        'Not Interested; Dead Deal'
      ]);
    }
  };

  const getPersonalCompPercentageOfTotal = (broker: string): number => {
    const rate = compRates.find(r => r.broker === broker);
    if (!rate) return 0;
    return (rate.comp_percentage * rate.personal_comp_percentage) / 100;
  };

  const calculateComp = (loanAmount: string, broker: string): number => {
    const amount = parsePurchasePrice(loanAmount);
    const personalCompPercentage = getPersonalCompPercentageOfTotal(broker);
    return (amount * personalCompPercentage) / 100;
  };

  const calculateCompFromBrokerComp = (brokerComp: string, broker: string, leadSource: string): number => {
    const brokerCompAmount = parsePurchasePrice(brokerComp);
    
    if (broker === 'NS') {
      return Math.round(brokerCompAmount * 0.75);
    } else if (broker === 'TM') {
      if (leadSource === 'TM Leads') {
        return Math.round(brokerCompAmount * 0.70);
      } else {
        return Math.round(brokerCompAmount * 0.85);
      }
    }
    
    return brokerCompAmount;
  };

  const fetchClosedClients = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('client_state', 'Closed')
        .order('date_created', { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      console.error('Error fetching closed clients:', err);
      setError('Failed to load closed clients');
    } finally {
      setLoading(false);
    }
  };

  const loadRealtors = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('realtors')
      .select('*')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading realtors:', error);
      return;
    }

    if (data && data.length > 0) {
      setRealtors(data);
    }
  };

  const getLeadSourceDisplay = (client: Client): string => {
    if (client.lead_source === 'Realtor' && client.realtor_id) {
      const realtor = realtors.find(r => r.id === client.realtor_id);
      return realtor ? realtor.name : 'Realtor';
    }
    if (client.lead_source === 'FreeRateUpdate' || !client.realtor_id) {
      return 'FreeRateUpdate';
    }
    return client.lead_source || 'Realtor';
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
  };

  const handleSave = async (id: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;

    // Determine final lender name
    const finalLenderName = editingLenderName === 'Other' ? editingCustomLender : (editingLenderName || client.lender_name);

    // Calculate comp from broker_comp if it exists
    let finalComp = client.comp;
    if (editingBrokerComp && editingBrokerComp.trim() !== '') {
      const calculatedComp = calculateCompFromBrokerComp(editingBrokerComp, client.broker, client.lead_source);
      finalComp = formatCurrency(calculatedComp);
    }

    const { error } = await supabase
      .from('clients')
      .update({
        name: client.name,
        email: client.email,
        phone: client.phone,
        state: client.state,
        purchase_price: client.purchase_price,
        broker: client.broker,
        last_contact: client.last_contact,
        client_state: client.client_state,
        loan_type: client.loan_type,
        loan_amount: client.loan_amount || null,
        lender_name: finalLenderName || null,
        comp: finalComp || null,
        interest_rate: client.interest_rate || null,
        lender_loan_number: client.lender_loan_number || null,
        arive_loan_number: client.arive_loan_number || null,
        broker_comp: editingBrokerComp || client.broker_comp || null
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client');
      return;
    }

    setClients(clients.map(c => 
      c.id === id ? { ...c, lender_name: finalLenderName, comp: finalComp, broker_comp: editingBrokerComp || c.broker_comp } : c
    ));

    setEditingId(null);
    setEditingLenderName('');
    setEditingCustomLender('');
    setEditingBrokerComp('');
  };

  const handleChange = async (id: string, field: keyof Client, value: string) => {
    const client = clients.find(c => c.id === id);
    if (!client) return;

    // Log state change to history if client_state is changing
    if (field === 'client_state' && value !== client.client_state) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase.from('client_state_history').insert({
          client_id: client.id,
          client_name: client.name,
          previous_state: client.client_state,
          new_state: value,
          user_id: session.user.id
        });
      }
    }

    setClients(clients.map(c => {
      if (c.id === id) {
        return { ...c, [field]: value };
      }
      return c;
    }));
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleSearch = () => {
    setInterestRateFilter(tempInterestRateFilter);
    setMonthsSinceCloseFilter(tempMonthsSinceCloseFilter);
  };

  const handleClearFilter = () => {
    setInterestRateFilter('');
    setMonthsSinceCloseFilter('');
    setTempInterestRateFilter('Select Rate');
    setTempMonthsSinceCloseFilter('Make Selection');
  };

  const handleDesktopSearch = () => {
    setDesktopInterestRateFilter(tempDesktopInterestRateFilter);
    setDesktopMonthsSinceCloseFilter(tempDesktopMonthsSinceCloseFilter);
    setDateFromFilter(tempDateFromFilter);
    setDateToFilter(tempDateToFilter);
  };

  const handleDesktopClearFilter = () => {
    setDesktopInterestRateFilter('');
    setDesktopMonthsSinceCloseFilter('');
    setTempDesktopInterestRateFilter('Select Rate');
    setTempDesktopMonthsSinceCloseFilter('Make Selection');
    setDateFromFilter('');
    setDateToFilter('');
    setTempDateFromFilter('');
    setTempDateToFilter('');
  };

  const handleClientNameClick = (client: Client) => {
    setClientInfoDialogClient(client);
    setShowClientInfoDialog(true);
  };

  // Calculate months between two dates
  const getMonthsSinceClose = (closingDate: string): number => {
    if (!closingDate || closingDate === '-') return 999; // Return large number if no date
    
    try {
      const closeDate = new Date(closingDate + 'T00:00:00');
      const today = new Date();
      
      const yearsDiff = today.getFullYear() - closeDate.getFullYear();
      const monthsDiff = today.getMonth() - closeDate.getMonth();
      
      return yearsDiff * 12 + monthsDiff;
    } catch (error) {
      return 999;
    }
  };

  // Filter clients based on interest rate, months since close, date range, and search query
  const filteredClients = clients.filter(client => {
    // Use desktop filters for desktop, mobile filters for mobile
    const activeInterestRateFilter = window.innerWidth >= 768 ? desktopInterestRateFilter : interestRateFilter;
    const activeMonthsSinceCloseFilter = window.innerWidth >= 768 ? desktopMonthsSinceCloseFilter : monthsSinceCloseFilter;
    const activeSearchQuery = window.innerWidth >= 768 ? desktopSearchQuery : mobileSearchQuery;
    
    // Interest rate filter - ignore if default value is selected
    if (activeInterestRateFilter && activeInterestRateFilter !== '' && activeInterestRateFilter !== 'Select Rate') {
      const filterRate = parseFloat(activeInterestRateFilter);
      if (!isNaN(filterRate)) {
        if (!client.interest_rate) return false;
        const clientRate = parseFloat(client.interest_rate);
        if (isNaN(clientRate) || clientRate <= filterRate) return false;
      }
    }
    
    // Months since close filter - changed to "over" instead of "within" - ignore if default value is selected
    if (activeMonthsSinceCloseFilter && activeMonthsSinceCloseFilter !== '' && activeMonthsSinceCloseFilter !== 'Make Selection') {
      const filterMonths = parseFloat(activeMonthsSinceCloseFilter);
      if (!isNaN(filterMonths)) {
        const monthsSince = getMonthsSinceClose(client.closing_date || '');
        if (monthsSince < filterMonths) return false; // Changed from > to <
      }
    }
    
    // Date range filter (only for desktop)
    if (window.innerWidth >= 768 && (dateFromFilter || dateToFilter)) {
      if (!client.closing_date || client.closing_date === '-') return false;
      
      try {
        const closingDate = new Date(client.closing_date + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // If only "from" date is filled, show from that date until today
        if (dateFromFilter && !dateToFilter) {
          const fromDate = new Date(dateFromFilter + 'T00:00:00');
          if (closingDate < fromDate || closingDate > today) return false;
        }
        // If only "to" date is filled, show anything until that date
        else if (!dateFromFilter && dateToFilter) {
          const toDate = new Date(dateToFilter + 'T00:00:00');
          if (closingDate > toDate) return false;
        }
        // If both dates are filled, show between the dates
        else if (dateFromFilter && dateToFilter) {
          const fromDate = new Date(dateFromFilter + 'T00:00:00');
          const toDate = new Date(dateToFilter + 'T00:00:00');
          if (closingDate < fromDate || closingDate > toDate) return false;
        }
      } catch (error) {
        return false;
      }
    }
    
    // Search filter
    if (activeSearchQuery && activeSearchQuery.trim() !== '') {
      const query = activeSearchQuery.toLowerCase().trim();
      const searchableFields = [
        client.name,
        client.email,
        client.phone,
        client.state,
        client.lender_name,
        client.loan_type,
        client.lead_source,
        client.lender_loan_number,
        client.arive_loan_number
      ].filter(Boolean).map(field => field?.toLowerCase() || '');
      
      return searchableFields.some(field => field.includes(query));
    }
    
    return true;
  });

  // Sort filtered clients
  const sortedAndFilteredClients = [...filteredClients].sort((a, b) => {
    if (!sortField) return 0;

    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    // Handle special cases for sorting
    if (sortField === 'purchase_price' || sortField === 'loan_amount') {
      aValue = parsePurchasePrice(aValue || '0');
      bValue = parsePurchasePrice(bValue || '0');
    } else if (sortField === 'interest_rate') {
      aValue = parseFloat(aValue || '0');
      bValue = parseFloat(bValue || '0');
    } else if (sortField === 'comp' || sortField === 'broker_comp') {
      aValue = parsePurchasePrice(aValue || '0');
      bValue = parsePurchasePrice(bValue || '0');
    } else if (sortField === 'closing_date') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    } else if (sortField === 'lead_source') {
      aValue = getLeadSourceDisplay(a);
      bValue = getLeadSourceDisplay(b);
    } else {
      // Convert to strings for comparison
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field: keyof Client) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExportToExcel = () => {
    // Prepare data for Excel
    const exportData = sortedAndFilteredClients.map(client => ({
      'Client Name': client.name,
      'State': getStateAbbreviation(client.state),
      'Purchase Price': client.purchase_price,
      'Loan Amount': client.loan_amount || '-',
      'Interest Rate': client.interest_rate ? `${parseFloat(client.interest_rate).toFixed(3)}%` : '-',
      'Broker Comp': client.broker_comp ? formatPurchasePrice(client.broker_comp) : '-',
      'Comp': client.broker_comp 
        ? formatCurrency(calculateCompFromBrokerComp(client.broker_comp, client.broker, client.lead_source))
        : client.comp 
          ? formatPurchasePrice(client.comp) 
          : client.loan_amount 
            ? formatCurrency(calculateComp(client.loan_amount, client.broker))
            : '-',
      'Lead Source': getLeadSourceDisplay(client),
      'Lender Name': client.lender_name || '-',
      'Loan Type': client.loan_type || '-',
      'Lender Loan Number': client.lender_loan_number || '-',
      'Arive Loan Number': client.arive_loan_number || '-',
      'Broker': client.broker,
      'Closing Date': formatDate(client.closing_date || '-'),
      'Client State': client.client_state || 'Closed'
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length, 15)
    }));
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Closed Clients');

    // Generate filename with current date
    const filename = `Closed_Clients_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Write file with .xlsx extension
    XLSX.writeFile(wb, filename);
  };

  // Add useEffect hooks to load data on mount
  useEffect(() => {
    const initializePage = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/login');
        return;
      }

      // Load user profile
      const { data: profile } = await supabase
        .from('clients')
        .select('name')
        .eq('user_id', session.user.id)
        .limit(1)
        .single();

      if (profile) {
        setUserFullName(profile.name);
      }

      // Load all data
      await Promise.all([
        fetchClosedClients(),
        loadRealtors(),
        loadLenderNames(),
        loadCompRates(),
        loadClientStates()
      ]);
    };

    initializePage();
  }, []);

  const renderSortableHeader = (primaryLabel: string, secondaryLabel: string | null, primaryField: keyof Client, secondaryField: keyof Client | null, columnKey: string) => {
    const isPrimarySorted = sortField === primaryField;
    const isSecondarySorted = secondaryField && sortField === secondaryField;
    
    return (
      <th className={`px-2 py-2 text-center font-semibold relative`} style={{ fontSize: '9.375px', width: `${columnWidths[columnKey]}px`, minWidth: '80px' }}>
        <div 
          className={`cursor-pointer hover:bg-gray-100 transition-colors rounded px-1 py-0.5 ${isPrimarySorted ? 'bg-gray-100' : ''}`}
          onClick={() => handleSort(primaryField)}
        >
          <div className={`flex items-center justify-center gap-1 ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`}>
            <span>{primaryLabel}</span>
            {isPrimarySorted && (
              <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line text-teal`}></i>
            )}
          </div>
        </div>
        {secondaryLabel && secondaryField && (
          <div 
            className={`cursor-pointer hover:bg-gray-100 transition-colors rounded px-1 py-0.5 mt-0.5 ${isSecondarySorted ? 'bg-gray-100' : ''}`}
            onClick={() => handleSort(secondaryField)}
          >
            <div className={`flex items-center justify-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <span>{secondaryLabel}</span>
              {isSecondarySorted && (
                <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line text-teal`}></i>
              )}
            </div>
          </div>
        )}
        <div
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal/50 transition-colors"
          onMouseDown={(e) => handleResizeStart(e, columnKey)}
        />
      </th>
    );
  };

  const renderSingleSortableHeader = (label: string, field: keyof Client, colorClass: string, columnKey: string) => {
    const isSorted = sortField === field;
    
    return (
      <th className={`px-2 py-2 text-center font-semibold relative`} style={{ fontSize: '9.375px', width: `${columnWidths[columnKey]}px`, minWidth: '80px' }}>
        <div 
          className={`cursor-pointer hover:bg-gray-100 transition-colors rounded px-1 py-0.5 ${isSorted ? 'bg-gray-100' : ''}`}
          onClick={() => handleSort(field)}
        >
          <div className={`flex items-center justify-center gap-1 ${colorClass}`}>
            <span>{label}</span>
            {isSorted && (
              <i className={`ri-arrow-${sortDirection === 'asc' ? 'up' : 'down'}-s-line text-teal`}></i>
            )}
          </div>
        </div>
        <div
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal/50 transition-colors"
          onMouseDown={(e) => handleResizeStart(e, columnKey)}
        />
      </th>
    );
  };

  // Calculate summary statistics for NS and TM using filtered clients
  const nsClients = sortedAndFilteredClients.filter(c => c.broker === 'NS');
  const nsPurchasePrice = nsClients.reduce((sum, client) => sum + parsePurchasePrice(client.purchase_price || '0'), 0);
  const nsLoanAmount = nsClients.reduce((sum, client) => sum + parsePurchasePrice(client.loan_amount || '0'), 0);
  const nsComp = nsClients.reduce((sum, client) => {
    if (client.comp) {
      return sum + parsePurchasePrice(client.comp);
    }
    if (client.loan_amount) {
      return sum + calculateComp(client.loan_amount, client.broker);
    }
    return sum;
  }, 0);

  const tmClients = sortedAndFilteredClients.filter(c => c.broker === 'TM');
  const tmPurchasePrice = tmClients.reduce((sum, client) => sum + parsePurchasePrice(client.purchase_price || '0'), 0);
  const tmLoanAmount = tmClients.reduce((sum, client) => sum + parsePurchasePrice(client.loan_amount || '0'), 0);
  const tmComp = tmClients.reduce((sum, client) => {
    if (client.comp) {
      return sum + parsePurchasePrice(client.comp);
    }
    if (client.loan_amount) {
      return sum + calculateComp(client.loan_amount, client.broker);
    }
    return sum;
  }, 0);

  // Calculate summary statistics using filtered clients
  const totalPurchasePrice = sortedAndFilteredClients.reduce((sum, client) => sum + parsePurchasePrice(client.purchase_price || '0'), 0);
  const totalLoanAmount = sortedAndFilteredClients.reduce((sum, client) => sum + parsePurchasePrice(client.loan_amount || '0'), 0);
  const totalComp = sortedAndFilteredClients.reduce((sum, client) => {
    if (client.comp) {
      return sum + parsePurchasePrice(client.comp);
    }
    if (client.loan_amount) {
      return sum + calculateComp(client.loan_amount, client.broker);
    }
    return sum;
  }, 0);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center p-4">{error}</div>;
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Navigation */}
      <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-home-heart-line text-3xl text-navy"></i>
            </div>
            <span className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-navy'}`}>{userFullName || 'Loans with Heena'}</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <a href="/" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-home-5-line mr-2"></i>
              Home
            </a>
            <a href="/crm" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-contacts-book-2-line mr-2"></i>
              CRM
            </a>
            
            {/* Pages Dropdown */}
            <div className="relative group">
              <button className={`${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold whitespace-nowrap cursor-pointer flex items-center gap-1`}>
                <i className="ri-pages-line mr-2"></i>
                Pages
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div className={`absolute top-full left-0 mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
                <a href="/closed" className={`block px-4 py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold cursor-pointer`}>
                  <i className="ri-archive-line mr-2"></i>
                  Closed
                </a>
                <a href="/dead-deals" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-close-circle-line mr-2"></i>
                  Dead Deals
                </a>
                <a href="/on-hold" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-pause-circle-line mr-2"></i>
                  On Hold
                </a>
                <a href="/templates" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-file-text-line mr-2"></i>
                  Templates
                </a>
                <a href="/intake" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-inbox-line mr-2"></i>
                  Message Intake
                </a>
                <a href="/history" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-history-line mr-2"></i>
                  History
                </a>
                <a href="/admin" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-settings-3-line mr-2"></i>
                  Admin
                </a>
              </div>
            </div>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`}></i>
            </button>
            
            <button
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Logout
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:text-navy transition-colors cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}
          >
            <div className="px-6 py-4 space-y-3">
              <a href="/" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-home-5-line mr-2"></i>
                Home
              </a>
              <a href="/crm" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-contacts-book-2-line mr-2"></i>
                CRM
              </a>
              <a href="/closed" className={`block py-2 ${isDarkMode ? 'text-teal' : 'text-navy'} font-semibold cursor-pointer`}>
                <i className="ri-archive-line mr-2"></i>
                Closed
              </a>
              <a href="/dead-deals" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-close-circle-line mr-2"></i>
                Dead Deals
              </a>
              <a href="/on-hold" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-pause-circle-line mr-2"></i>
                On Hold
              </a>
              <a href="/templates" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-file-text-line mr-2"></i>
                Templates
              </a>
              <a href="/intake" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-inbox-line mr-2"></i>
                Message Intake
              </a>
              <a href="/history" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-history-line mr-2"></i>
                History
              </a>
              <a href="/admin" className={`block py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}>
                <i className="ri-settings-3-line mr-2"></i>
                Admin
              </a>
              <button
                onClick={toggleDarkMode}
                className={`block w-full text-left py-2 ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium cursor-pointer`}
              >
                <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} mr-2`}></i>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left py-2 text-red-600 hover:text-red-700 transition-colors font-medium cursor-pointer"
              >
                <i className="ri-logout-box-line mr-2"></i>
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="max-w-[1750px] mx-auto px-6 py-8">
        <div className="mb-8 text-center">
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} mb-1`}>Closed Clients</h1>
          <p className="text-sm text-gray-600">View all successfully closed client deals</p>
        </div>

        {/* Export Button */}
        <div className="mb-4 flex justify-end">
          <button
            onClick={handleExportToExcel}
            className={`${isDarkMode ? 'bg-green-700 hover:bg-green-800' : 'bg-green-600 hover:bg-green-700'} text-white px-5 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 text-sm`}
          >
            <i className="ri-file-excel-2-line text-base"></i>
            Export to Excel
          </button>
        </div>

        {/* Filters */}
        <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-4 mb-6`}>
          {/* Desktop Filters */}
          <div className="hidden md:flex items-center gap-6 flex-wrap">
            {/* Interest Rate Filter */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`}>Show rates above:</span>
                <select
                  value={tempDesktopInterestRateFilter}
                  onChange={(e) => setTempDesktopInterestRateFilter(e.target.value)}
                  className={`w-32 px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                >
                  <option value="Select Rate">Select Rate</option>
                  {Array.from({ length: 33 }, (_, i) => {
                    const value = (3 + i * 0.125).toFixed(3);
                    return (
                      <option key={value} value={value}>
                        {value}%
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* MonthsSince Close Filter */}
            <div className="flex items-center gap-4">
              <label className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'} whitespace-nowrap`}>Months since close:</label>
              <div className="flex items-center gap-2">
                <select
                  value={tempDesktopMonthsSinceCloseFilter}
                  onChange={(e) => setTempDesktopMonthsSinceCloseFilter(e.target.value)}
                  className={`w-32 px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                >
                  <option value="Make Selection">Make Selection</option>
                  {Array.from({ length: 12 }, (_, i) => {
                    const value = (i + 1).toString();
                    return (
                      <option key={value} value={value}>
                        {value} {value === '1' ? 'month' : 'months'}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="flex items-center gap-4">
              <label className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'} whitespace-nowrap`}>Closing date:</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={tempDateFromFilter}
                  onChange={(e) => setTempDateFromFilter(e.target.value)}
                  className={`w-40 px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                  placeholder="From"
                />
                <span className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`}>to</span>
                <input
                  type="date"
                  value={tempDateToFilter}
                  onChange={(e) => setTempDateToFilter(e.target.value)}
                  className={`w-40 px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                  placeholder="To"
                />
              </div>
            </div>

            {/* Search Button */}
            <button
              onClick={handleDesktopSearch}
              className="px-4 py-2 bg-teal text-white rounded-lg hover:bg-teal/90 transition-colors cursor-pointer font-medium text-sm whitespace-nowrap"
            >
              <i className="ri-search-line mr-2"></i>
              Search
            </button>

            {/* Clear Filter Button */}
            <button
              onClick={handleDesktopClearFilter}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer font-medium text-sm whitespace-nowrap"
            >
              <i className="ri-close-line mr-2"></i>
              Clear Filter
            </button>

            {/* Results Count */}
            <div className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'} text-center`}>
              Showing {sortedAndFilteredClients.length} of {clients.length} closed clients
            </div>

            {/* Search Field */}
            <div className="ml-auto flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={desktopSearchQuery}
                  onChange={(e) => setDesktopSearchQuery(e.target.value)}
                  className={`w-64 pl-10 pr-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb] placeholder-gray-500' : 'border-gray-300 placeholder-gray-400'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                />
                <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
              </div>
              {desktopSearchQuery && (
                <button
                  onClick={() => setDesktopSearchQuery('')}
                  className={`w-8 h-8 flex items-center justify-center ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} transition-colors cursor-pointer`}
                  title="Clear search"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>
          </div>

          {/* Mobile Filters */}
          <div className="md:hidden space-y-4">
            {/* Interest Rate Filter */}
            <div className="flex items-center gap-2">
              <span className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'} whitespace-nowrap`}>Show rates above:</span>
              <select
                value={tempInterestRateFilter}
                onChange={(e) => setTempInterestRateFilter(e.target.value)}
                className={`flex-1 px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
              >
                <option value="Select Rate">Select Rate</option>
                {Array.from({ length: 33 }, (_, i) => {
                  const value = (3 + i * 0.125).toFixed(3);
                  return (
                    <option key={value} value={value}>
                      {value}%
                    </option>
                  );
                })}
              </select>
            </div>

            {/* MonthsSince Close Filter */}
            <div className="flex items-center gap-2">
              <label className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'} whitespace-nowrap`}>Months since close:</label>
              <select
                value={tempMonthsSinceCloseFilter}
                onChange={(e) => setTempMonthsSinceCloseFilter(e.target.value)}
                className={`flex-1 px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
              >
                <option value="Make Selection">Make Selection</option>
                {Array.from({ length: 12 }, (_, i) => {
                  const value = (i + 1).toString();
                  return (
                    <option key={value} value={value}>
                      {value} {value === '1' ? 'month' : 'months'}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Search Field */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search clients..."
                value={mobileSearchQuery}
                onChange={(e) => setMobileSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-10 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb] placeholder-gray-500' : 'border-gray-300 placeholder-gray-400'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
              />
              <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
              {mobileSearchQuery && (
                <button
                  onClick={() => setMobileSearchQuery('')}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} transition-colors cursor-pointer`}
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="w-full px-4 py-2 bg-teal text-white rounded-lg hover:bg-teal/90 transition-colors cursor-pointer font-medium text-sm"
            >
              <i className="ri-search-line mr-2"></i>
              Search
            </button>

            {/* Clear Filter Button */}
            <button
              onClick={handleClearFilter}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer font-medium text-sm"
            >
              <i className="ri-close-line mr-2"></i>
              Clear Filter
            </button>

            {/* Results Count - Center Aligned */}
            <div className="text-sm text-gray-600 text-center pt-2">
              Showing {sortedAndFilteredClients.length} of {clients.length} closed clients
            </div>
          </div>
        </div>

        {/* Closed Clients Table */}
        <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border overflow-hidden mb-8`}>
          <div className="overflow-x-auto">
            {/* Desktop Table */}
            <table className="w-full hidden md:table" style={{ fontSize: '9.375px', tableLayout: 'fixed' }}>
              <thead className={`${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                <tr>
                  {renderSingleSortableHeader('Client Name', 'name', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'name')}
                  {renderSingleSortableHeader('State', 'state', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'state')}
                  {renderSortableHeader('Purchase Price', 'Loan Amount', 'purchase_price', 'loan_amount', 'purchase_price')}
                  {renderSingleSortableHeader('Interest Rate', 'interest_rate', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'interest_rate')}
                  {renderSortableHeader('Broker Comp', 'Comp', 'broker_comp', 'comp', 'broker_comp')}
                  {renderSingleSortableHeader('Lead Source', 'lead_source', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'lead_source')}
                  {renderSortableHeader('Lender Name', 'Loan Type', 'lender_name', 'loan_type', 'lender_name')}
                  {renderSortableHeader('Lender Loan #', 'Arive Loan #', 'lender_loan_number', 'arive_loan_number', 'lender_loan_number')}
                  <th className={`px-2 py-2 text-center font-semibold relative ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: `${columnWidths.broker}px`, minWidth: '80px' }}>
                    <div>Broker</div>
                    <div>Abbreviation</div>
                    <div
                      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal/50 transition-colors"
                      onMouseDown={(e) => handleResizeStart(e, 'broker')}
                    />
                  </th>
                  {renderSingleSortableHeader('Closing Date', 'closing_date', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'closing_date')}
                  {renderSingleSortableHeader('Client State', 'client_state', isDarkMode ? 'text-gray-400' : 'text-gray-600', 'client_state')}
                  <th className={`px-2 py-2 text-center font-semibold relative ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px', width: `${columnWidths.actions}px`, minWidth: '80px' }}>
                    Actions
                    <div
                      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-teal/50 transition-colors"
                      onMouseDown={(e) => handleResizeStart(e, 'actions')}
                    />
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'} divide-y`}>
                {sortedAndFilteredClients.map((client, index) => (
                  <tr key={client.id} className={`${isDarkMode ? (index % 2 === 0 ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-900 hover:bg-gray-700') : 'hover:bg-gray-50'} transition-colors`}>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => handleClientNameClick(client)}
                        className="font-medium text-teal hover:text-teal/80 underline cursor-pointer"
                        style={{ fontSize: '9.375px' }}
                      >
                        {client.name}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {editingId === client.id ? (
                        <input
                          type="text"
                          value={client.state}
                          onChange={(e) => handleChange(client.id, 'state', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                          style={{ fontSize: '9.375px' }}
                        />
                      ) : (
                        <span className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{getStateAbbreviation(client.state) || '-'}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {editingId === client.id ? (
                        <div className="space-y-1">
                          <input
                            type="text"
                            value={client.purchase_price}
                            onChange={(e) => handleChange(client.id, 'purchase_price', e.target.value)}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                            style={{ fontSize: '9.375px' }}
                          />
                          <input
                            type="text"
                            value={client.loan_amount || ''}
                            onChange={(e) => handleChange(client.id, 'loan_amount', e.target.value)}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                            style={{ fontSize: '9.375px' }}
                          />
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <div className={`font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>{formatPurchasePrice(client.purchase_price) || '-'}</div>
                          <div className={`font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{client.loan_amount ? formatPurchasePrice(client.loan_amount) : '-'}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {editingId === client.id ? (
                        <div className="relative">
                          <input
                            type="text"
                            value={client.interest_rate || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                handleChange(client.id, 'interest_rate', value);
                              }
                            }}
                            className={`w-full px-2 py-1 pr-6 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                            style={{ fontSize: '9.375px' }}
                          />
                          <span className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-sm`}>%</span>
                        </div>
                      ) : (
                        <span className={`font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>
                          {client.interest_rate ? `${parseFloat(client.interest_rate).toFixed(3)}%` : '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {editingId === client.id ? (
                        <div className="space-y-1">
                          <input
                            type="text"
                            value={editingBrokerComp || client.broker_comp || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, '');
                              if (value) {
                                const formatted = `$${parseInt(value).toLocaleString()}`;
                                setEditingBrokerComp(formatted);
                              } else {
                                setEditingBrokerComp('');
                              }
                            }}
                            onFocus={() => {
                              if (!editingBrokerComp && client.broker_comp) {
                                setEditingBrokerComp(client.broker_comp);
                              }
                            }}
                            placeholder="$0"
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                            style={{ fontSize: '9.375px' }}
                          />
                          <input
                            type="text"
                            value={client.comp || ''}
                            onChange={(e) => handleChange(client.id, 'comp', e.target.value)}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                            style={{ fontSize: '9.375px' }}
                          />
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <div className={`font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>
                            {client.broker_comp ? formatPurchasePrice(client.broker_comp) : '-'}
                          </div>
                          <div className="font-semibold text-teal" style={{ fontSize: '9.375px' }}>
                            {client.broker_comp 
                              ? formatCurrency(calculateCompFromBrokerComp(client.broker_comp, client.broker, client.lead_source))
                              : client.comp 
                                ? formatPurchasePrice(client.comp) 
                                : client.loan_amount 
                                  ? formatCurrency(calculateComp(client.loan_amount, client.broker))
                                  : '-'}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{getLeadSourceDisplay(client)}</span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {editingId === client.id ? (
                        <div className="space-y-1">
                          <div className="flex flex-col gap-1">
                            <select
                              value={editingLenderName}
                              onChange={(e) => {
                                setEditingLenderName(e.target.value);
                                if (e.target.value !== 'Other') {
                                  setEditingCustomLender('');
                                  handleChange(client.id, 'lender_name', e.target.value);
                                }
                              }}
                              className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                              style={{ fontSize: '9.375px' }}
                            >
                              {lenderOptions.map((lender) => (
                                <option key={lender} value={lender}>{lender}</option>
                              ))}
                              <option value="Other">Other</option>
                            </select>
                            {editingLenderName === 'Other' && (
                              <input
                                type="text"
                                placeholder="Enter lender name"
                                value={editingCustomLender}
                                onChange={(e) => {
                                  setEditingCustomLender(e.target.value);
                                  handleChange(client.id, 'lender_name', e.target.value);
                                }}
                                className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                                style={{ fontSize: '9.375px' }}
                              />
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <div className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>{client.lender_name || '-'}</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{client.loan_type || '-'}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {editingId === client.id ? (
                        <div className="space-y-1">
                          <input
                            type="text"
                            value={client.lender_loan_number || ''}
                            onChange={(e) => handleChange(client.id, 'lender_loan_number', e.target.value)}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                            style={{ fontSize: '9.375px' }}
                          />
                          <input
                            type="text"
                            value={client.arive_loan_number || ''}
                            onChange={(e) => handleChange(client.id, 'arive_loan_number', e.target.value)}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                            style={{ fontSize: '9.375px' }}
                          />
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <div className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`} style={{ fontSize: '9.375px' }}>{client.lender_loan_number || '-'}</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{client.arive_loan_number || '-'}</div>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-medium ${
                        client.broker === 'NS' 
                          ? 'bg-teal/10 text-teal' 
                          : 'bg-orange-100 text-orange-700'
                      }`} style={{ fontSize: '9.375px' }}>
                        {client.broker || '-'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {editingId === client.id ? (
                        <input
                          type="date"
                          value={client.closing_date || ''}
                          onChange={(e) => handleChange(client.id, 'closing_date', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-center`}
                          style={{ fontSize: '9.375px' }}
                        />
                      ) : (
                        <span className={`${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-600'}`} style={{ fontSize: '9.375px' }}>{formatDate(client.closing_date || '-')}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {editingId === client.id ? (
                        <select
                          value={client.client_state}
                          onChange={(e) => handleChange(client.id, 'client_state', e.target.value)}
                          className={`px-1.5 py-0.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer text-center`}
                          style={{ fontSize: '9.375px' }}
                        >
                          {clientStateOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium" style={{ fontSize: '9.375px' }}>
                          {client.client_state || 'Closed'}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-center gap-1">
                        {editingId === client.id ? (
                          <>
                            <button
                              onClick={() => handleSave(client.id)}
                              className="w-5 h-5 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                              title="Save"
                            >
                              <i className="ri-check-line text-xs"></i>
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className={`w-5 h-5 flex items-center justify-center ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-lg hover:bg-gray-200 transition-colors cursor-pointer`}
                              title="Cancel"
                            >
                              <i className="ri-close-line text-xs"></i>
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleEdit(client.id)}
                            className="w-5 h-5 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-xs"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-700">
              {sortedAndFilteredClients.map((client, index) => (
                <div key={client.id} className={`p-4 ${isDarkMode ? (index % 2 === 0 ? 'bg-[#212936]' : 'bg-[#121826]') : 'hover:bg-gray-50'} transition-colors`}>
                  {/* Line 1: Client Name, Broker Abbreviation, Loan Amount, Closing Date, Interest Rate */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      {editingId === client.id ? (
                        <input
                          type="text"
                          value={client.name}
                          onChange={(e) => handleChange(client.id, 'name', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <button
                          onClick={() => handleClientNameClick(client)}
                          className="font-semibold text-teal hover:text-teal/80 underline cursor-pointer text-sm truncate text-left"
                        >
                          {client.name || '-'}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium text-xs ${
                        client.broker === 'NS' 
                          ? 'bg-teal/10 text-teal' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {client.broker || '-'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className={`text-xs ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-500'} mb-0.5`}>Loan Amount</div>
                      {editingId === client.id ? (
                        <input
                          type="text"
                          value={client.loan_amount || ''}
                          onChange={(e) => handleChange(client.id, 'loan_amount', e.target.value)}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <div className={`font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} text-sm`}>
                          {client.loan_amount ? formatPurchasePrice(client.loan_amount) : '-'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className={`text-xs ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-500'} mb-0.5 text-right`}>Interest Rate</div>
                      {editingId === client.id ? (
                        <div className="relative">
                          <input
                            type="text"
                            value={client.interest_rate || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                handleChange(client.id, 'interest_rate', value);
                              }
                            }}
                            className={`w-full px-2 py-1 pr-6 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                          />
                          <span className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs`}>%</span>
                        </div>
                      ) : (
                        <div className={`font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} text-sm text-right`}>
                          {client.interest_rate ? `${parseFloat(client.interest_rate).toFixed(3)}%` : '-'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Line 2: Lender Name and Loan Type (right aligned) */}
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <div className={`text-xs ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-500'} mb-0.5`}>Lender</div>
                      <div className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} truncate`}>{client.lender_name || '-'}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xs ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-500'} mb-0.5`}>Loan Type</div>
                      <div className={`text-sm ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} truncate`}>{client.loan_type || '-'}</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className={`flex items-center justify-end gap-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    {editingId === client.id ? (
                      <>
                        <button
                          onClick={() => handleSave(client.id)}
                          className="px-3 py-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer text-xs font-medium"
                        >
                          <i className="ri-check-line mr-1"></i>
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className={`px-3 py-1.5 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-xs font-medium`}
                        >
                          <i className="ri-close-line mr-1"></i>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEdit(client.id)}
                        className="px-3 py-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer text-xs font-medium"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {sortedAndFilteredClients.length === 0 && (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No closed clients found matching the selected filters
              </div>
            )}
          </div>
        </div>

        {/* Summary Boxes - Moved below the table */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Closed Clients */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-teal/10 rounded-lg">
                <i className="ri-check-double-line text-2xl text-teal"></i>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Closed Clients</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Clients:</span>
                <span className="text-2xl font-bold text-gray-900">{sortedAndFilteredClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Purchase Price Total:</span>
                <span className="text-xl font-bold text-teal">{formatCurrency(totalPurchasePrice)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Loan Amount Total:</span>
                <span className="text-xl font-bold text-teal">{formatCurrency(totalLoanAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Comp Total:</span>
                <span className="text-xl font-bold text-teal">{formatCurrency(totalComp)}</span>
              </div>
            </div>
          </motion.div>

          {/* NS (Next Step Lending) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-black rounded-xl shadow-sm border border-gray-800 p-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div>
                <p className="text-sm text-[#54a7dc] font-medium">NS (Next Step Lending)</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Clients:</span>
                <span className="text-2xl font-bold text-white">{nsClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Purchase Price Total:</span>
                <span className="text-xl font-bold text-[#54a7dc]">{formatCurrency(nsPurchasePrice)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Loan Amount Total:</span>
                <span className="text-xl font-bold text-[#54a7dc]">{formatCurrency(nsLoanAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Comp Total:</span>
                <span className="text-xl font-bold text-[#54a7dc]">{formatCurrency(nsComp)}</span>
              </div>
            </div>
          </motion.div>

          {/* TM (Trademark Financing) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#f3e8c5] rounded-xl shadow-sm border border-[#e5d9b0] p-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div>
                <p className="text-sm text-[#1c272b] font-medium">TM (Trademark Financing)</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Clients:</span>
                <span className="text-2xl font-bold text-[#1c272b]">{tmClients.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Purchase Price Total:</span>
                <span className="text-xl font-bold text-[#1c272b]">{formatCurrency(tmPurchasePrice)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Loan Amount Total:</span>
                <span className="text-xl font-bold text-[#1c272b]">{formatCurrency(tmLoanAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5a5a5a]">Comp Total:</span>
                <span className="text-xl font-bold text-[#1c272b]">{formatCurrency(tmComp)}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Client Info Dialog */}
      {showClientInfoDialog && clientInfoDialogClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Client Information</h3>
                <button
                  onClick={() => setShowClientInfoDialog(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">{clientInfoDialogClient.name}</h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <a 
                    href={`mailto:${clientInfoDialogClient.email}`}
                    className="text-sm text-teal hover:text-teal/80 font-medium cursor-pointer underline"
                  >
                    {clientInfoDialogClient.email}
                  </a>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <a 
                    href={`tel:${clientInfoDialogClient.phone.replace(/\D/g, '')}`}
                    className="text-sm text-teal hover:text-teal/80 font-medium cursor-pointer underline"
                  >
                    {formatPhoneNumber(clientInfoDialogClient.phone)}
                  </a>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <p className="text-sm text-gray-900">{getStateAbbreviation(clientInfoDialogClient.state)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Price</label>
                  <p className="text-sm text-gray-900">{formatPurchasePrice(clientInfoDialogClient.purchase_price)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loan Amount</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.loan_amount ? formatPurchasePrice(clientInfoDialogClient.loan_amount) : '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interest Rate</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.interest_rate ? `${clientInfoDialogClient.interest_rate}%` : '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loan Type</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.loan_type || '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lead Source</label>
                  <p className="text-sm text-gray-900">{getLeadSourceDisplay(clientInfoDialogClient)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lender Name</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.lender_name || '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Broker</label>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full font-medium text-sm ${
                    clientInfoDialogClient.broker === 'NS' 
                      ? 'bg-teal/10 text-teal' 
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {clientInfoDialogClient.broker}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Closing Date</label>
                  <p className="text-sm text-gray-900">{formatDate(clientInfoDialogClient.closing_date || '-')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Comp</label>
                  <p className="text-sm font-semibold text-teal">
                    {clientInfoDialogClient.broker_comp 
                      ? formatCurrency(calculateCompFromBrokerComp(clientInfoDialogClient.broker_comp, clientInfoDialogClient.broker, clientInfoDialogClient.lead_source))
                      : clientInfoDialogClient.comp 
                        ? formatPurchasePrice(clientInfoDialogClient.comp) 
                        : clientInfoDialogClient.loan_amount 
                          ? formatCurrency(calculateComp(clientInfoDialogClient.loan_amount, clientInfoDialogClient.broker))
                          : '-'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Broker Comp</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.broker_comp ? formatPurchasePrice(clientInfoDialogClient.broker_comp) : '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lender Loan Number</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.lender_loan_number || '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Arive Loan Number</label>
                  <p className="text-sm text-gray-900">{clientInfoDialogClient.arive_loan_number || '-'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Contact</label>
                  <p className="text-sm text-gray-900">{formatDateTime(clientInfoDialogClient.last_contact || '-')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client State Updated</label>
                  <p className="text-sm text-gray-900">{formatDateTime(clientInfoDialogClient.client_state_updated || '-')}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
